import React, { useEffect, useState, useCallback } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Layout from "../layout/Layout";
import ContentWrapper from "../components/ContentWrapper";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import API from "../Config/ApiConfig";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";

const PLAN_TYPES = [
  { label: "All", value: "" },
  { label: "Premium", value: "premium" },
  { label: "Medium", value: "medium" },
  { label: "Basic", value: "basic" },
];

function exportToCSV(data) {
  if (!data.length) return;
  const header = [
    "Name",
    "Contact",
    "Flight Number",
    "Departure Date",
    "Departure Airport",
    "Destination Airport",
    "Plan Type",
  ];
  const rows = data.map((carer) => [
    `${carer.first_name} ${carer.last_name}`,
    `${carer.email}\n${carer.phone?.country_code || ""} ${
      carer.phone?.number || ""
    }`,
    carer.flight_details?.flight_number || "",
    carer.flight_details?.departure_date
      ? new Date(carer.flight_details.departure_date).toLocaleDateString()
      : "",
    carer.flight_details?.departure_airport_name
      ? `${carer.flight_details.departure_airport_name} (${carer.flight_details.departure_airport})`
      : carer.flight_details?.departure_airport || "",
    carer.flight_details?.destination_airport_name
      ? `${carer.flight_details.destination_airport_name} (${carer.flight_details.destination_airport})`
      : carer.flight_details?.destination_airport || "",
    (carer.packages || []).join(", "),
  ]);
  let csvContent =
    "data:text/csv;charset=utf-8," +
    [header, ...rows].map((e) => e.map((v) => `"${v}"`).join(",")).join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", "carer_availability.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function CarerAvailability() {
  const [carers, setCarers] = useState([]);
  const [filteredCarers, setFilteredCarers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [planType, setPlanType] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [email, setEmail] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [editingCarer, setEditingCarer] = useState(null);
  const [editPackages, setEditPackages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [detailsCarer, setDetailsCarer] = useState(null);
  
  // Track if current filters came from URL parameters (don't persist these)
  const [filtersFromURL, setFiltersFromURL] = useState(false);

  // Persist filters to session storage (only if not from URL)
  const persistFilters = useCallback(() => {
    // Don't persist filters if they came from URL parameters
    if (filtersFromURL) {
      return;
    }
    
    sessionStorage.setItem("carerAvailabilityFilters", JSON.stringify({
      search,
      planType,
      origin,
      destination,
      travelDate,
      email,
      sortOrder
    }));
  }, [filtersFromURL, search, planType, origin, destination, travelDate, email, sortOrder]);

  // Clear all filters
  const clearFilters = () => {
    setSearch('');
    setPlanType('');
    setOrigin('');
    setDestination('');
    setTravelDate('');
    setEmail('');
    setSortOrder('asc');
    setFiltersFromURL(false); // Reset URL flag when clearing
    sessionStorage.removeItem("carerAvailabilityFilters");
  };

  // Helper function to get active filters summary
  const getActiveFilters = () => {
    const filters = [];
    if (search) filters.push(`Search: "${search}"`);
    if (planType) filters.push(`Plan: ${planType.charAt(0).toUpperCase() + planType.slice(1)}`);
    if (origin) filters.push(`Origin: ${origin}`);
    if (destination) filters.push(`Destination: ${destination}`);
    if (travelDate) filters.push(`Date: ${new Date(travelDate).toLocaleDateString()}`);
    if (email) filters.push(`Email: "${email}"`);
    return filters;
  };

  const activeFilters = getActiveFilters();
  const hasActiveFilters = activeFilters.length > 0;

  // Initialize filters from URL parameters or session storage
  useEffect(() => {
    let initialSearch = "";
    let initialPlanType = "";
    let initialOrigin = "";
    let initialDestination = "";
    let initialTravelDate = "";
    let initialEmail = "";
    let initialSortOrder = "asc";

    // First, check for URL parameters (these take priority and won't be persisted)
    if (searchParams.size > 0) {
      // Parse URL parameters
      if (searchParams.get('search')) {
        initialSearch = searchParams.get('search');
        setSearch(initialSearch);
      }
      
      if (searchParams.get('planType')) {
        const planParam = searchParams.get('planType');
        if (['premium', 'medium', 'basic'].includes(planParam)) {
          initialPlanType = planParam;
          setPlanType(initialPlanType);
        }
      }
      
      if (searchParams.get('origin')) {
        initialOrigin = searchParams.get('origin').toUpperCase();
        setOrigin(initialOrigin);
      }
      
      if (searchParams.get('destination')) {
        initialDestination = searchParams.get('destination').toUpperCase();
        setDestination(initialDestination);
      }
      
      if (searchParams.get('travelDate')) {
        initialTravelDate = searchParams.get('travelDate');
        setTravelDate(initialTravelDate);
      }
      
      if (searchParams.get('email')) {
        initialEmail = searchParams.get('email');
        setEmail(initialEmail);
      }
      
      if (searchParams.get('sortOrder')) {
        const orderParam = searchParams.get('sortOrder');
        if (['asc', 'desc'].includes(orderParam)) {
          initialSortOrder = orderParam;
          setSortOrder(initialSortOrder);
        }
      }
      
      setFiltersFromURL(true);
    } else {
      // No URL parameters, check session storage
      const persisted = sessionStorage.getItem("carerAvailabilityFilters");
      if (persisted) {
        try {
          const p = JSON.parse(persisted);
          initialSearch = p.search || "";
          initialPlanType = p.planType || "";
          initialOrigin = p.origin || "";
          initialDestination = p.destination || "";
          initialTravelDate = p.travelDate || "";
          initialEmail = p.email || "";
          initialSortOrder = p.sortOrder || "asc";
          
          setSearch(initialSearch);
          setPlanType(initialPlanType);
          setOrigin(initialOrigin);
          setDestination(initialDestination);
          setTravelDate(initialTravelDate);
          setEmail(initialEmail);
          setSortOrder(initialSortOrder);
        } catch (e) {
          console.warn('Error parsing persisted filters:', e);
        }
      }
      
      setFiltersFromURL(false);
    }
  }, [searchParams]);

  // Fetch carers when filters change
  useEffect(() => {
    setLoading(true);
    const params = [];
    if (origin) params.push(`origin=${encodeURIComponent(origin)}`);
    if (destination)
      params.push(`destination=${encodeURIComponent(destination)}`);
    if (travelDate)
      params.push(`travel_date=${encodeURIComponent(travelDate)}`);
    const query = params.length ? `?${params.join("&")}` : "";
    API.get(`/carers/availability${query}`)
      .then((res) => {
        setCarers(res.data.data || []);
        setLoading(false);
        persistFilters(); // Persist filters after successful fetch
      })
      .catch((err) => {
        setError("Failed to fetch carers");
        setLoading(false);
      });
  }, [origin, destination, travelDate, persistFilters]);

  // Local search and plan type filter
  useEffect(() => {
    let filtered = carers;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    filtered = filtered.filter((c) => {
      const d = c.flight_details?.departure_date
        ? new Date(c.flight_details.departure_date)
        : null;
      return d && d >= now;
    });
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          `${c.first_name} ${c.last_name}`.toLowerCase().includes(s) ||
          (c.email && c.email.toLowerCase().includes(s))
      );
    }
    if (email) {
      const e = email.toLowerCase();
      filtered = filtered.filter(
        (c) => c.email && c.email.toLowerCase().includes(e)
      );
    }
    if (planType) {
      filtered = filtered.filter(
        (c) =>
          Array.isArray(c.packages) &&
          c.packages.includes(planType)
      );
    }

    filtered = filtered.sort((a, b) => {
      const da = new Date(a.flight_details?.departure_date || 0).getTime();
      const db = new Date(b.flight_details?.departure_date || 0).getTime();
      return sortOrder === "asc" ? da - db : db - da;
    });

    setFilteredCarers(filtered);
    persistFilters(); // Persist filters after filtering
  }, [search, planType, email, carers, sortOrder, persistFilters]);

  return (
    <Layout>
      <ContentWrapper title="Carer Availability">
          {/* Collapsible Filter Bar */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
            {/* Filter Header - Always Visible */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setFiltersExpanded(!filtersExpanded)}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                  {!filtersExpanded && hasActiveFilters && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {activeFilters.map((filter, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800">
                          {filter}
                        </span>
                      ))}
                    </div>
                  )}
                  {!filtersExpanded && !hasActiveFilters && (
                    <p className="text-sm text-gray-500">Click to expand filter options</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">
                  {filteredCarers.length} result{filteredCarers.length !== 1 ? 's' : ''} found
                </div>
                <svg 
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Expandable Filter Content */}
            {filtersExpanded && (
              <div className="px-4 pb-4 border-t border-gray-200">
                {/* Search Bar - Full Width */}
                <div className="mb-4 mt-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                      }}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    />
                    {search && (
                      <button
                        onClick={() => {
                          setSearch('');
                          setFiltersFromURL(false); // Reset URL flag when clearing search
                        }}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        Plan Type
                      </span>
                    </label>
                    <select
                      value={planType}
                      onChange={(e) => {
                        setPlanType(e.target.value);
                        setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors bg-white"
                    >
                      {PLAN_TYPES.map((pt) => (
                        <option key={pt.value} value={pt.value}>
                          {pt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Origin Airport
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. MEL, SYD"
                      value={origin}
                      onChange={(e) => {
                        setOrigin(e.target.value.toUpperCase());
                        setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors uppercase"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Destination Airport
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. DEL, BOM"
                      value={destination}
                      onChange={(e) => {
                        setDestination(e.target.value.toUpperCase());
                        setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors uppercase"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Travel Date
                      </span>
                    </label>
                    <input
                      type="date"
                      value={travelDate}
                      onChange={(e) => {
                        setTravelDate(e.target.value);
                        setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-1 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                        Email Filter
                      </span>
                    </label>
                    <input
                      type="email"
                      placeholder="e.g. john@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setFiltersFromURL(false); // Reset URL flag when user manually changes filters
                      }}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={clearFilters}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Clear Filters
                    </button>
                    
                    {hasActiveFilters && (
                      <div className="text-sm text-gray-500">
                        {activeFilters.length} filter{activeFilters.length !== 1 ? 's' : ''} active
                      </div>
                    )}
                  </div>

                  <button
                    className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    onClick={() => exportToCSV(filteredCarers)}
                    disabled={filteredCarers.length === 0}
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export CSV ({filteredCarers.length})
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Enhanced Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Carer Availability ({filteredCarers.length})
              </h3>
            </div>

            {/* Table Content */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Carer
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Contact Info
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Flight Details
                    </th>
                    <th 
                      className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:text-emerald-600 transition-colors"
                      onClick={() => {
                        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
                        setFiltersFromURL(false); // Reset URL flag when user manually changes sort
                      }}
                      title={`Sort by departure date (${sortOrder === "asc" ? "Earliest first" : "Latest first"})`}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Departure Date</span>
                        <svg className={`w-4 h-4 ${sortOrder === "asc" ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Route & Plan
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mr-3"></div>
                          <span className="text-gray-500">Loading carers...</span>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Error Loading Data</h3>
                        <p className="mt-1 text-sm text-red-600">{error}</p>
                      </td>
                    </tr>
                  ) : filteredCarers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No carers found</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {search || planType || origin || destination || travelDate
                            ? "Try adjusting your search or filter criteria."
                            : "No available carers match the current criteria."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredCarers.map((carer, i) => (
                      <tr
                        key={carer._id}
                        className="hover:bg-gray-50 transition-colors duration-200 group"
                      >
                        {/* Carer Column */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="relative">
                              <img
                                src={carer.profile_picture}
                                alt="profile"
                                className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-emerald-300 transition-all duration-200"
                                onError={(e) => { e.currentTarget.src = '/default-avatar.png'; }}
                              />
                              {carer.shufti_verification && (
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                  <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p 
                                className="text-sm font-semibold text-gray-900 truncate cursor-pointer hover:text-emerald-600 transition-colors"
                                onClick={() => navigate(`/users/${carer._id}`)}
                              >
                                {carer.first_name} {carer.last_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                ID: {carer._id?.slice(-8)}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Contact Info Column */}
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="flex items-center text-sm text-gray-900">
                              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                              </svg>
                              <span className="truncate max-w-[150px]" title={carer?.email}>
                                {carer?.email}
                              </span>
                            </div>
                            {carer?.phone?.country_code && (
                              <div className="flex items-center text-sm text-gray-600">
                                <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span className="text-xs">
                                  {carer?.phone?.country_code} {carer?.phone?.number}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Flight Details Column */}
                        <td className="px-6 py-4">
                          <div className="space-y-1 text-sm">
                            <div className="flex items-center text-gray-900">
                              <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                              </svg>
                              <span className="font-medium">
                                {carer.flight_details?.flight_number || 'N/A'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 ml-6">
                              Flight Details
                            </div>
                          </div>
                        </td>

                        {/* Departure Date Column */}
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="text-gray-900 font-medium">
                              {carer.flight_details?.departure_date
                                ? new Date(carer.flight_details.departure_date).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric", 
                                    year: "numeric"
                                  })
                                : "N/A"}
                            </div>
                            <div className="text-xs text-gray-500">
                              {carer.flight_details?.departure_date
                                ? new Date(carer.flight_details.departure_date).toLocaleDateString("en-US", {
                                    weekday: "short"
                                  })
                                : ""}
                            </div>
                          </div>
                        </td>

                        {/* Route & Plan Column */}
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <div className="text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-500">FROM:</span>
                                <span className="font-medium">
                                  {carer.flight_details?.departure_airport || 'N/A'}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-xs font-medium text-gray-500">TO:</span>
                                <span className="font-medium">
                                  {carer.flight_details?.destination_airport || 'N/A'}
                                </span>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(carer.packages) &&
                                carer.packages.map((pkg) => (
                                  <span
                                    key={pkg}
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      pkg === 'premium' 
                                        ? 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800'
                                        : pkg === 'medium'
                                        ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'
                                        : 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800'
                                    }`}
                                  >
                                    {pkg.charAt(0).toUpperCase() + pkg.slice(1)}
                                  </span>
                                ))}
                            </div>
                          </div>
                        </td>

                        {/* Status Column */}
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {carer.availability_status === 'fully_booked' || carer.availability_status === 'assigned' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Assigned
                              </span>
                            ) : carer.availability_status === 'cancelled' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                Cancelled
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Available
                              </span>
                            )}
                            {carer.assigned_booking && (
                              <div className="text-xs text-gray-500">
                                #{carer.assigned_booking.booking_number}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Actions Column */}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center space-x-2">
                            {/* View Details Button */}
                            <button
                              onClick={() => setDetailsCarer(carer)}
                              className="group relative p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                              title="View Details"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                View Details
                              </span>
                            </button>

                            {/* Edit Packages Button */}
                            <button
                              onClick={() => {
                                setEditingCarer(carer);
                                setEditPackages(carer.packages || []);
                              }}
                              className="group relative p-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                              title="Edit Packages"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                Edit Packages
                              </span>
                            </button>

                            {/* View Profile Button */}
                            <button
                              onClick={() => navigate(`/users/${carer._id}`)}
                              className="group relative p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                              title="View Profile"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                View Profile
                              </span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
      </ContentWrapper>
      {/* Enhanced Edit Modal */}
      {editingCarer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 transform transition-all">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <svg className="w-5 h-5 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Packages
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {editingCarer.first_name} {editingCarer.last_name}
              </p>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              <div className="space-y-3 mb-4">
                <p className="text-sm text-gray-700 mb-4">Select the plan types this carer can offer:</p>
                {["basic", "medium", "premium"].map((pkg) => (
                  <label
                    key={pkg}
                    className="flex items-center cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors border border-gray-200"
                  >
                    <input
                      type="checkbox"
                      checked={editPackages.includes(pkg)}
                      onChange={() => {
                        setEditPackages((prev) =>
                          prev.includes(pkg)
                            ? prev.filter((p) => p !== pkg)
                            : [...prev, pkg]
                        );
                      }}
                      className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                    />
                    <div className="flex items-center justify-between w-full">
                      <span className="text-sm font-medium text-gray-700">
                        {pkg.charAt(0).toUpperCase() + pkg.slice(1)} Plan
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          pkg === 'premium' 
                            ? 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-800'
                            : pkg === 'medium'
                            ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'
                            : 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800'
                        }`}
                      >
                        {pkg.charAt(0).toUpperCase() + pkg.slice(1)}
                      </span>
                    </div>
                  </label>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-800">Package Requirements</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Select between 1 and 3 packages. Each package represents a different service level the carer can provide.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setEditingCarer(null);
                  setEditPackages([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  if (editPackages.length === 0) {
                    alert("Please select at least one package");
                    return;
                  }
                  if (editPackages.length > 3) {
                    alert("You can select maximum 3 packages");
                    return;
                  }

                  try {
                    setSaving(true);

                    await API.put(`/carers/${editingCarer._id}/packages`, {
                      packages: editPackages,
                    });
                    setCarers((prev) =>
                      prev.map((c) =>
                        c._id === editingCarer._id
                          ? {
                              ...c,
                              packages: editPackages,
                              flight_details: {
                                ...c.flight_details,
                                packages: editPackages,
                              },
                            }
                          : c
                      )
                    );

                    setEditingCarer(null);
                    setEditPackages([]);
                  } catch (err) {
                    alert("Failed to update packages. Please try again.");
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving || editPackages.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </div>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {detailsCarer && (
        <Dialog open={!!detailsCarer} onClose={() => setDetailsCarer(null)} maxWidth="md" fullWidth>
          <Box sx={{ position: "relative" }}>
           
            <DialogTitle sx={{ pb: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Flight Details
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  {detailsCarer.first_name} {detailsCarer.last_name}
                </Typography>
                <i
                  className={detailsCarer.shufti_verification ? "fas fa-check-circle" : "fas fa-times-circle"}
                  data-tooltip-id="verification-tip"
                  data-tooltip-content={detailsCarer.shufti_verification ? "Verified user" : "Not verified"}
                  style={{
                    marginLeft: 6,
                    fontSize: "1rem",
                    color: detailsCarer.shufti_verification ? "#16a34a" : "#dc2626",
                    lineHeight: 1
                  }}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Flight Number
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {detailsCarer.flight_details?.flight_number || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Departure Date
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {detailsCarer.flight_details?.departure_date
                      ? new Date(detailsCarer.flight_details.departure_date).toLocaleDateString()
                      : "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Origin
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {detailsCarer.flight_details?.departure_airport_name
                      ? `${detailsCarer.flight_details.departure_airport_name} (${detailsCarer.flight_details.departure_airport})`
                      : detailsCarer.flight_details?.departure_airport || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    Destination
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {detailsCarer.flight_details?.destination_airport_name
                      ? `${detailsCarer.flight_details.destination_airport_name} (${detailsCarer.flight_details.destination_airport})`
                      : detailsCarer.flight_details?.destination_airport || "-"}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                {(detailsCarer.packages || []).map((pkg) => (
                  <Chip
                    key={pkg}
                    label={pkg.charAt(0).toUpperCase() + pkg.slice(1)}
                    sx={{
                      background:
                        pkg === "premium"
                          ? "linear-gradient(90deg, #2B8B6F, #17C470)"
                          : pkg === "medium"
                          ? "linear-gradient(90deg, #034328, #2B8B6F)"
                          : "linear-gradient(90deg, #FFD700, #FFA500)",
                      color: "#fff",
                      fontWeight: 600,
                      borderRadius: "14px",
                      height: "24px",
                      ".MuiChip-label": { px: 1.25 }
                    }}
                  />
                ))}
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Layovers
              </Typography>
              {Array.isArray(detailsCarer.flight_details?.layovers) && detailsCarer.flight_details.layovers.length > 0 ? (
                <Box sx={{ position: "relative", ml: 1 }}>
                  {detailsCarer.flight_details.layovers.map((l, idx) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                      <Box sx={{ position: "relative", mr: 2 }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            backgroundColor: "#2B8B6F",
                            mt: 0.7,
                          }}
                        />
                        {idx < detailsCarer.flight_details.layovers.length - 1 && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              left: 4,
                              width: 2,
                              height: "calc(100% - 12px)",
                              backgroundColor: "#e5e7eb",
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          border: "1px solid #eee",
                          borderRadius: 2,
                          p: 1.5,
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <Box sx={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: 1 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: "#666" }}>
                              Airport
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              {l.airport_name || l.airport_code || "-"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "#666" }}>
                              City
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              {l.city || "-"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "#666" }}>
                              Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              {l.date ? new Date(l.date).toLocaleDateString() : "-"}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: "#666" }}>
                              Flight
                            </Typography>
                            <Typography sx={{ fontWeight: 600 }}>
                              {l.flight_number || "-"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "#666" }}>
                  No layovers for this itinerary.
                </Typography>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <button
                  onClick={() => setDetailsCarer(null)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "none",
                    background: "#2B8B6F",
                    color: "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Close
                </button>
              </Box>
            </DialogContent>
          </Box>
        </Dialog>
      )}
      <ReactTooltip id="verification-tip" place="top" />
    </Layout>
  );
}
